import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { trackPageView, trackButtonClick, trackUserJourney } from '../utils/analytics';
import { trackSubscriptionScreenViewed } from '../utils/funnelTracking';
import { formatMarketingCount } from '../utils/formatMarketingCount';
import config from '../config';
import axios from 'axios';

interface SubscribeProps {
    userAuth: boolean;
    isSubscribed: boolean;
    subscription?: string;
    children?: React.ReactNode;
}

const Subscribe = ({ userAuth, isSubscribed, subscription, children }: SubscribeProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [approvedCount, setApprovedCount] = useState<number>(150);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Track page view
    useEffect(() => {
        trackPageView('Subscribe', 'Choose Your Subscription Plan');
        trackUserJourney('subscribe_page_view', 1, {
            userAuth: userAuth ? 'yes' : 'no',
            isSubscribed: isSubscribed ? 'yes' : 'no'
        });
        // Funnel tracking
        trackSubscriptionScreenViewed();
    }, [userAuth, isSubscribed]);

    // Redirect if user is already subscribed
    useEffect(() => {
        if (userAuth && isSubscribed && subscription && subscription !== 'none') {
            navigate('/sponsors');
        }
    }, [userAuth, isSubscribed, subscription, navigate]);

    // Fetch approved sponsor count for dynamic display (fallback 150)
    useEffect(() => {
        let mounted = true;
        axios.get(`${config.backendUrl}sponsors/db-info`).then(res => {
            if (!mounted) return;
            setApprovedCount(res.data?.sponsors || 150);
        }).catch(() => setApprovedCount(150));
        return () => { mounted = false; };
    }, []);

    const sponsorsDisplay = formatMarketingCount(approvedCount);

    const handleSubscribe = async () => {
        setLoading(true);
        setError('');

        try {
            trackButtonClick('subscribe', 'subscribe');
            trackUserJourney('subscribe_selected', 2);

            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/signup');
                return;
            }

            const response = await axios.post(`${config.backendUrl}users/checkout`, {}, {
                headers: {
                    'x-auth-token': token
                }
            });

            // Redirect to Stripe checkout
            const stripe = await import('@stripe/stripe-js');
            const stripePromise = stripe.loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_live_51MpGntBKPgChhmNg9wLgFqQICAjXSVAzaEMRKwXjuLQeZZhwghaiA7VDoG0Cov9uEnDGF9RlAKQkQ1xXPSooAX8D00Mp9uCFyO");
            const stripeInstance = await stripePromise;

            await stripeInstance?.redirectToCheckout({
                sessionId: response.data.sessionId
            });

        } catch (error: any) {
            console.error('Error creating checkout session:', error);
            setError(error.response?.data || 'Error creating checkout session. Please try again.');
            trackUserJourney('checkout_error', 3, { error: error.message });
        } finally {
            setLoading(false);
        }
    };

    // Same markup/classes as the homepage pricing card (Home.tsx's #pricing
    // section) so this page and the landing page never drift apart again.
    return (
        <div className="web-page">
            <div className="web-section pricing-new-section mt-0" id="subscribe">
                <div className="web-section__container web-section-content">
                    <div className="subscribe-container">
                        <div className="subscribe-header">
                            <h1 className="subscribe-title">
                                Get instant access to SponsorDB
                            </h1>
                            <p className="subscribe-subtitle">
                                Built for independent newsletter creators with 1K–100K subscribers.
                            </p>
                        </div>

                        <div className="sdb-pricing">
                            <div className="sdb-pricing-card">
                                <div className="sdb-pricing-card__badge">Cancel Anytime</div>

                                <div className="sdb-pricing-card__header">
                                    <h3 className="sdb-pricing-card__title">Sponsor Access</h3>
                                    <div className="sdb-pricing-card__price">
                                        <span className="sdb-pricing-card__currency">$</span>
                                        <span className="sdb-pricing-card__amount">20</span>
                                        <span className="sdb-pricing-card__period">/month</span>
                                    </div>
                                </div>

                                <ul className="sdb-pricing-card__benefits">
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Access to {sponsorsDisplay} verified sponsors, not a preview — the full database</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Direct contact emails to real decision makers, not generic inboxes</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>AI-drafted, personalized pitch emails for every sponsor</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Smart sponsor matching based on your newsletter's niche and audience</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Filter by industry, audience size, and newsletter fit</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Track your outreach — contacted, responded, closed</span>
                                    </li>
                                </ul>

                                <button
                                    className="btn btn-primary btn-lg btn-full sdb-pricing-card__cta"
                                    onClick={handleSubscribe}
                                    disabled={loading}
                                >
                                    {loading ? 'Processing...' : 'Subscribe Now'}
                                </button>
                                <p className="sdb-pricing-card__note">Billed monthly • Cancel anytime</p>
                            </div>
                        </div>

                        {error && (
                            <div className="subscribe-error">
                                {error}
                            </div>
                        )}

                        {/* Trust Indicators */}
                        <div className="subscribe-trust">
                            <div className="subscribe-trust__item">
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <span>{sponsorsDisplay} verified sponsors</span>
                            </div>
                            <div className="subscribe-trust__item">
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <span>Verified contact information</span>
                            </div>
                            <div className="subscribe-trust__item">
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <span>Cancel anytime</span>
                            </div>
                        </div>

                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Subscribe;
